import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "Components/Form";
import {
  AppHeaderCell,
  AppTableCell,
  AppTable,
  AppTableBody,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
} from "Components/AppTable";
import {
  MilitaryReport,
  MilitaryReportDescription,
  MilitaryReportTitle,
  TemplateTypeBadge,
  TemplateTypeBadgeDot,
} from "./styles";
import { MenuItem } from "@mui/material";
import { Report } from "Models/report";
import { Checklist } from "Models/checklist";
import { useAppState } from "Context/StateContext";
import { MILITARY_REPORTS_NAMES } from "Constants";

interface ReportsTableProps {
  reports?: Report[];
  checklists?: Checklist[];
}

enum ReportAssigned {
  CUSTOM = "Custom",
  DEFAULT = "Default",
}

export const ReportsTable = ({ reports, checklists }: ReportsTableProps) => {
  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();

  const columnHeaders = {
    report: translate("report"),
    template: translate("templateType"),
    assigned: translate("assignedTemplate"),
  };

  let reportsTemplatesConfig: {
    [reportName: string]: { description: string; template: string; assigned: string };
  } = {};

  const isCustomReport = (reportName: string) => {
    return (
      MILITARY_REPORTS_NAMES.some(
        (militaryReportName) =>
          militaryReportName.toLowerCase().trim() === reportName.toLowerCase().trim()
      ) && !!currentUser?.company?.isMilitary
    );
  };

  reports?.forEach((report) => {
    if (report.name) {
      reportsTemplatesConfig[report.name] = {
        description: report.description || "",
        template: isCustomReport(report.name)
          ? translate("customTemplate")
          : translate("shipeeziDefault2"),
        assigned: isCustomReport(report.name) ? ReportAssigned.CUSTOM : ReportAssigned.DEFAULT,
      };
    }
  });

  checklists?.forEach((checklist) => {
    if (checklist.code) {
      const checklistProperty = checklist.description || checklist.code;

      reportsTemplatesConfig[checklistProperty] = {
        description: checklist.description || checklist.code,
        template: translate("shipeeziDefault2"),
        assigned: ReportAssigned.DEFAULT,
      };
    }
  });

  const fieldKeys = Object.keys(reportsTemplatesConfig) as Array<
    keyof typeof reportsTemplatesConfig
  >;

  return (
    <AppTableContainer>
      <AppTable>
        <AppTableHead>
          <AppTableRow>
            <AppHeaderCell>{columnHeaders.report}</AppHeaderCell>
            <AppHeaderCell>{columnHeaders.template}</AppHeaderCell>
            <AppHeaderCell>{columnHeaders.assigned}</AppHeaderCell>
          </AppTableRow>
        </AppTableHead>
        <AppTableBody>
          {fieldKeys.map((fieldKey) => {
            const fieldConfig = reportsTemplatesConfig[fieldKey];

            const displayName = translate(fieldKey.toString());

            return (
              <AppTableRow key={fieldKey} style={{ height: "64px" }}>
                <AppTableCell>
                  <MilitaryReport>
                    <MilitaryReportTitle>{displayName}</MilitaryReportTitle>
                    <MilitaryReportDescription>{fieldConfig.description}</MilitaryReportDescription>
                  </MilitaryReport>
                </AppTableCell>
                <AppTableCell>
                  <TemplateTypeBadge isCustom={isCustomReport(fieldKey.toString())}>
                    <TemplateTypeBadgeDot isCustom={isCustomReport(fieldKey.toString())} />
                    {fieldConfig.template}
                  </TemplateTypeBadge>
                </AppTableCell>
                <AppTableCell>
                  <Select disabled value={fieldConfig.assigned} onChange={() => {}}>
                    <MenuItem key={fieldConfig.description} value={ReportAssigned.DEFAULT}>
                      <span>{translate("shipeeziDefault2")}</span>
                    </MenuItem>
                    <MenuItem key={fieldConfig.description} value={ReportAssigned.CUSTOM}>
                      <span>{translate("clientCustomTemplate")}</span>
                    </MenuItem>
                  </Select>
                </AppTableCell>
              </AppTableRow>
            );
          })}
        </AppTableBody>
      </AppTable>
    </AppTableContainer>
  );
};
